import './plugins/veevalidate'
import './plugins/vuetify'
import './plugins/vue-gtm'
import 'moment-duration-format'

import globalAxios, { AxiosError, AxiosRequestConfig } from 'axios'

import App from './App.vue'
import { AxiosHttpClient } from 'coloquent/dist/httpclient/axios/AxiosHttpClient'
import { Model } from 'coloquent'
import Notification from '@/classes/Notification'
import Vue from 'vue'
import i18n from './i18n'
import initializeAmplify from './plugins/amplify'
// @ts-ignore
import localization from 'moment/locale/nl'
import moment from 'moment'
import router from './router'
import { store } from '@/store'
import { v4 as uuidv4 } from 'uuid'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'

moment.locale('nl', localization)

initializeAmplify(process.env)
Vue.config.productionTip = false

const axiosClient = new AxiosHttpClient()
const modelAxios = axiosClient.getImplementingClient()

modelAxios.interceptors.request.use(addTraceId)
globalAxios.interceptors.request.use(addTraceId)
modelAxios.interceptors.request.use(addAuthHeader)
globalAxios.interceptors.request.use(addAuthHeader)
modelAxios.interceptors.response.use((response) => response, handleAxiosErrors)
modelAxios.interceptors.response.use((response) => response, refreshToken)
globalAxios.interceptors.response.use((response) => response, handleAxiosErrors)
globalAxios.interceptors.response.use((response) => response, refreshToken)

modelAxios.defaults.headers['Accept-Language'] = i18n.locale

async function addAuthHeader(config) {
	// skip adding headers when on the register/activate route
	if (['register', 'login'].includes(router.currentRoute?.name ?? '')) {
		return config
	}
	try {
		if (!window.localStorage.getItem('isTesting')) {
			await getCurrentUser()
			const { tokens } = await fetchAuthSession({ forceRefresh: true })
			config.headers.Authorization = `Bearer ${tokens?.accessToken}`
		}

		return config
	} catch (e) {
		// skip two cognito exceptions
		if (e !== 'No current user' && (e as any).code !== 'NotAuthorizedException') {
			throw e
		}
		return config
	}
}
async function addTraceId(req) {
	if (!req) return req
	req.headers['X-4S-Trace-Id'] = uuidv4()

	return req
}
async function refreshToken(error: AxiosError): Promise<any> {
	const originalRequest = error.config

	// @ts-expect-error [expects _retry to be defined on originalRequest]
	if (error.response?.status !== 401 || originalRequest?._retry) {
		return Promise.reject(error)
	}

	// @ts-ignore
	originalRequest._retry = true

	const { tokens } = await fetchAuthSession({ forceRefresh: true })
	if (tokens?.accessToken) {
		Model.getHttpClient().getImplementingClient().defaults.headers.Authorization = `Bearer ${tokens.accessToken}`
		// @ts-ignore
		originalRequest.headers = originalRequest.headers.toJSON()
		// @ts-ignore
		globalAxios.defaults.headers.Authorization = `Bearer ${tokens.accessToken}`
		// @ts-ignore
		originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`

		return globalAxios(originalRequest as AxiosRequestConfig)
	}

	await Notification.Error(vue.$t('errors.unknown').toString())
	return Promise.reject(error)
}

async function handleAxiosErrors(error: AxiosError): Promise<AxiosError> {
	if (error?.name === 'UserUnAuthenticatedException') {
		// do nothing, we will be redirected to login page automatically
		return Promise.reject(error)
	}
	if (!error.response) {
		await Notification.Error(vue.$t('errors.generic').toString())
	} else if (error.response.status === 500) {
		await Notification.Error(vue.$t('errors.unknown').toString())
	} else if (error.response.status === 400) {
		let errorData = error.response.data as any

		if (errorData instanceof Blob) {
			errorData = JSON.parse(await errorData.text())
		}

		// Temporary fix for cognito errors; should be reformatted in backend
		if (
			Object.prototype.hasOwnProperty.call(errorData, 'data') &&
			!Object.prototype.hasOwnProperty.call(errorData, 'errors')
		) {
			errorData = errorData.data
		}

		if (errorData?.errors?.[0].length) {
			await Notification.Error(
				`<p>${i18n.t('errors.form_errors')}</p><ul><li>${errorData.errors[0]
					.map((error) => error.title)
					.join('</li><li>')}</li></ul>`
			)
		}
	} else if (error.response.status === 403) {
		await router.replace({ name: 'forbidden' })
	} else if (error.response.status === 498) {
		// TODO: handle 498 (Unauthorized from remote API like exact-online)
	}
	return Promise.reject(error)
}

Model.setHttpClient(axiosClient)

const vue = new Vue({
	router,
	store,
	i18n,
	render: (h) => h(App)
}).$mount('#app')

Vue.config.errorHandler = (error, vm, info) => {
	if (error.name?.includes('UserUnAuthenticatedException')) {
		// do nothing, we will be redirected to login page automatically
		return
	}
	// @ts-ignore
	if (window.Cypress && window.top) {
		// @ts-ignore
		window.top.console.error(error)
	}

	// eslint-disable-next-line
	console.error(error)

	if (error.message?.includes('ChunkLoadError')) {
		const reload = window.confirm(i18n.t('errors.chunkLoadError').toString())
		if (reload) {
			window.location.reload()
		}
	}
	Notification.Error(vue.$t('errors.generic', { error: error.message }).toString())
}

window.onresize = () => {
	store.commit('viewport/setWidth', window.innerWidth)
}

if (!window.localStorage.getItem('isTesting')) {
	// @ts-ignore
	const OneSignal = window.OneSignal || []
	// eslint-disable-next-line no-unused-expressions
	if (OneSignal.push) {
		OneSignal.push(function () {
			OneSignal.init({
				appId: process.env.VUE_APP_ONESIGNAL_APP_ID,
				notifyButton: {
					enable: false
				},
				allowLocalhostAsSecureOrigin: true
			})
		})
	}
}
